import type {
  CharacterId,
  ChatId,
  Entity,
  LorebookId,
  PersonaId,
  PresetId,
  ParticipantId,
} from "./common.js";
import type { SenderType } from "./message.js";

/**
 * A participant seat in a chat (decision #22: flexible sender model).
 * 1:1 chat = one character participant + one user(persona) participant.
 * Group chat = multiple character participants. Storing participants as a
 * list now means group chat is purely an orchestration/UI addition later,
 * with no schema migration.
 */
export interface ChatParticipant {
  readonly id: ParticipantId;
  type: SenderType;
  /** Set when type === "character". */
  characterId?: CharacterId;
  /** Set when type === "user". */
  personaId?: PersonaId;
  /** Display-name override for this seat (else use the entity's name). */
  displayName?: string;
  /** Inactive participants stay in the roster but don't take turns. */
  muted?: boolean;
}

/**
 * Strategy for choosing the next speaker in a multi-participant chat
 * (decision #22). For 1:1 this is irrelevant ("manual" with one character).
 */
export type TurnStrategy = "manual" | "round-robin" | "auto";

/**
 * Which game module owns this session (decision #25/#28). Well-known string
 * keys ("roleplay", "text-adventure", "stat-rpg", "visual-novel", ...) — NOT a
 * branded UUID, because modules are identified by stable names, not instances.
 */
export type ModuleId = string;

/** The canonical roleplay module id. Absence of `Chat.moduleId` means this. */
export const ROLEPLAY_MODULE_ID: ModuleId = "roleplay";

/**
 * A chat/session. Holds participants, config, and pointers — but NOT the
 * messages (those are separate rows, decision #9). Branching is a lightweight
 * session fork (decision #16): forking copies message rows up to a point into
 * a new chat, which is cheap because messages are row-addressable.
 *
 * Generalized to a game session (decision #28): `moduleId` selects the owning
 * game module and `moduleState` holds that module's per-session state. The
 * fields are additive/optional — a Chat without them is a roleplay session
 * (the original behavior), so existing data needs no migration of content.
 */
export interface Chat extends Entity {
  readonly id: ChatId;

  /** User-facing title (often auto-derived from the first character). */
  title: string;

  /**
   * Owning game module (decision #25/#28). Optional for backward compat:
   * absent ⇒ {@link ROLEPLAY_MODULE_ID}. New sessions set it explicitly.
   */
  moduleId?: ModuleId;

  /**
   * Module-specific session state blob (decision #28). Shape is defined by the
   * module's stateSchema; the platform stores it opaquely. Roleplay leaves it
   * unset (its state is participants + messages). Serializable for sync (#20)
   * and future multiplayer (#31).
   */
  moduleState?: Record<string, unknown>;

  /** Participant roster (decision #22). */
  participants: ChatParticipant[];

  /** Next-speaker strategy for multi-participant chats. */
  turnStrategy: TurnStrategy;

  /** Generation preset in effect (decision #12). */
  presetId?: PresetId;

  /** Lorebooks attached at the chat level (in addition to character-embedded). */
  lorebookIds: LorebookId[];

  /** Author's note text injected per the pipeline (decision #12). */
  authorNote?: string;
  /** Depth at which the author's note is injected. */
  authorNoteDepth?: number;

  /**
   * Fork lineage (decision #16). If this chat was forked from another, this
   * records the parent chat and the message order at the fork point.
   */
  forkedFrom?: { chatId: ChatId; atOrder: number };

  /** Last activity timestamp for sorting the chat list. */
  lastMessageAt?: number;
  /** Cached count for list display without scanning messages. */
  messageCount?: number;

  /** Pinned/archived flags for organization. */
  pinned?: boolean;
  archived?: boolean;

  extensions?: Record<string, unknown>;
}
